import { watch, onBeforeUnmount, toValue, type MaybeRefOrGetter } from 'vue';
import { usePositions } from '@/stores/usePositions';
import type { PnlPositionFilters } from '@/types/pnl.types';

/**
 * Composable that automatically manages positions store based on account parameter
 * @param account - The account ID (can be reactive)
 */
export function usePositionsManager(account: MaybeRefOrGetter<string | undefined>) {
  // Initialize positions store
  const positionsStore = usePositions();

  // Default filters for event contracts
  const defaultFilters: PnlPositionFilters = {
    only_event_contracts: true,
    field_mask: [
      'daily_pnl',
      'market_value',
      'unrealized_pnl',
      // `avg_cost` and `cost_basis` are prefetched so the order ticket overlay can reuse this PnL subscription instead of unsub/resubscribing.
      'avg_cost',
      'cost_basis',
      'ec_descr_1',
      'ec_descr_2',
      'put_or_call',
      'event_product_conid',
      'security_exchange',
      'opposite_conid',
      'strike_label',
      'underlying_name',
    ],
  };

  const stopWatching = watch(
    () => toValue(account),
    (accountParam) => {
      if (accountParam && accountParam !== positionsStore.accountId) {
        positionsStore.setAccountId(accountParam, defaultFilters);
      } else if (!accountParam && positionsStore.accountId) {
        positionsStore.setAccountId('', undefined);
      }
    },
    { immediate: true },
  );

  // Cleanup function
  const cleanup = () => {
    stopWatching();
  };

  // Auto cleanup on unmount
  onBeforeUnmount(cleanup);

  // Return store and cleanup function for manual control if needed
  return {
    positionsStore,
    cleanup,
  };
}
